import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import "./UploadCard.css";
import "./ProfilePage.css";
import {
  cacheProfileLists,
  flushPendingBookListUpdates,
  getCachedProfileLists
} from "../utils/bookListCache";

// Module-level cache — survives navigation to sub-pages and back
let _profileCache = null;

const COLLECTION_CARDS = [
  {
    key: "bookmarks",
    title: "Bookmarks",
    subtitle: "Books with reviews and notes attached",
    path: "/bookmarks"
  },
  {
    key: "finished",
    title: "Finished List",
    subtitle: "Your completed books and fics.",
    path: "/finished"
  },
  {
    key: "tbr",
    title: "To Be Read",
    subtitle: "Books and fics you want to start next.",
    path: "/tbr"
  }
];

const PROFILE_MODAL_CLOSE_MS = 180;

function getProgressLabel(progress) {
  const numeric = Number(progress || 0);
  if (numeric <= 0) return "1%";
  return `${Math.round(numeric)}%`;
}

function getProgressWidth(progress) {
  const numeric = Number(progress || 0);
  if (numeric <= 0) return "1%";
  return `${Math.min(100, numeric)}%`;
}

function getProfileHandle(user) {
  if (user?.username) return user.username;
  return "";
}

function formatDisplayName(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "Reader";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function getProfilePictureUrl(profilePicture) {
  if (!profilePicture) return null;
  if (/^https?:\/\//i.test(profilePicture)) return profilePicture;
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const baseUrl = apiUrl.replace("/api", "");
  return `${baseUrl}/${profilePicture}`;
}

function ProfilePage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") || "{}")
  );
  const [recentRead, setRecentRead] = useState(_profileCache?.recentRead ?? null);
  const [recentLoading, setRecentLoading] = useState(_profileCache === null);
  const [listSummary, setListSummary] = useState(
    _profileCache?.listSummary ?? {
      bookmarks: { count: 0, items: [] },
      tbr: { count: 0, items: [] },
      finished: { count: 0, items: [] }
    }
  );
  const [activeModal, setActiveModal] = useState(null);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    displayName: "",
    username: ""
  });
  const [selectedProfileFile, setSelectedProfileFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [profileSaveError, setProfileSaveError] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const fileInputRef = useRef(null);
  const closeTimerRef = useRef(null);
  const profilePreviewUrlRef = useRef("");

  const fetchRecentRead = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const currentUserId = currentUser?.id || 0;

    if (!apiUrl || !currentUserId) {
      setRecentRead(null);
      setRecentLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/get-recents.php?uid=${currentUserId}`, {
        credentials: "include",
        cache: "no-store"
      });
      const data = await response.json();
      if (data?.success && Array.isArray(data.recents)) {
        const next = data.recents[0] || null;
        setRecentRead(next);
        _profileCache = { ..._profileCache, recentRead: next };
      }
    } catch (error) {
      console.error("Failed to fetch recent read:", error);
    } finally {
      setRecentLoading(false);
    }
  }, [currentUser?.id]);

  const fetchListSummary = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const currentUserId = currentUser?.id || 0;

    if (!apiUrl || !currentUserId) {
      setListSummary({
        bookmarks: { count: 0, items: [] },
        tbr: { count: 0, items: [] },
        finished: { count: 0, items: [] }
      });
      return;
    }

    const cachedSummary = getCachedProfileLists(currentUserId);
    if (cachedSummary) {
      setListSummary(cachedSummary);
      _profileCache = { ..._profileCache, listSummary: cachedSummary };
    }

    if (!navigator.onLine) return;

    try {
      await flushPendingBookListUpdates(apiUrl);
      const response = await fetch(`${apiUrl}/get-profile-lists.php?uid=${currentUserId}`, {
        credentials: "include",
        cache: "no-store"
      });
      const data = await response.json();
      if (data?.success && data.lists) {
        const next = {
          bookmarks: data.lists.bookmarks || { count: 0, items: [] },
          tbr: data.lists.tbr || { count: 0, items: [] },
          finished: data.lists.finished || { count: 0, items: [] }
        };
        setListSummary(next);
        cacheProfileLists(currentUserId, next);
        _profileCache = { ..._profileCache, listSummary: next };
      }
    } catch (error) {
      console.error("Failed to fetch profile lists:", error);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    document.body.style.backgroundColor = "#ffffff";
    return () => {
      document.body.style.backgroundColor = "";
      window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleProfileUpdated = (event) => {
      const nextUser = event.detail;
      if (nextUser) {
        setCurrentUser(nextUser);
      }
    };

    window.addEventListener("profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("profile-updated", handleProfileUpdated);
  }, []);

  useEffect(() => {
    fetchRecentRead();
  }, [fetchRecentRead]);

  useEffect(() => {
    fetchListSummary();
  }, [fetchListSummary]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchRecentRead();
      }
    };

    window.addEventListener("reader-closed", fetchRecentRead);
    window.addEventListener("reader-progress-saved", fetchRecentRead);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("reader-closed", fetchRecentRead);
      window.removeEventListener("reader-progress-saved", fetchRecentRead);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchRecentRead]);

  useEffect(() => {
    const refreshLists = () => {
      fetchListSummary();
    };

    window.addEventListener("reader-closed", refreshLists);
    window.addEventListener("reader-progress-saved", refreshLists);
    window.addEventListener("book-lists-updated", refreshLists);

    return () => {
      window.removeEventListener("reader-closed", refreshLists);
      window.removeEventListener("reader-progress-saved", refreshLists);
      window.removeEventListener("book-lists-updated", refreshLists);
    };
  }, [fetchListSummary]);

  useEffect(() => {
    if (!activeModal) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyWidth = document.body.style.width;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.width = previousBodyWidth;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      window.scrollTo(0, scrollY);
    };
  }, [activeModal]);

  useEffect(() => {
    profilePreviewUrlRef.current = profilePreviewUrl;
  }, [profilePreviewUrl]);

  useEffect(() => {
    if (activeModal !== "edit") {
      if (profilePreviewUrlRef.current && profilePreviewUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(profilePreviewUrlRef.current);
      }
      setProfilePreviewUrl("");
      setSelectedProfileFile(null);
      setProfileSaveError("");
      return;
    }

    setProfileForm({
      displayName: currentUser?.displayName || "",
      username: currentUser?.username || ""
    });
    setProfilePreviewUrl(currentUser?.profilePicture || "");
    setSelectedProfileFile(null);
    setProfileSaveError("");
  }, [activeModal, currentUser?.displayName, currentUser?.profilePicture, currentUser?.username]);

  const openModal = (modalName) => {
    window.clearTimeout(closeTimerRef.current);
    setIsModalClosing(false);
    setActiveModal(modalName);
  };

  const requestCloseModal = () => {
    if (!activeModal || isModalClosing) return;
    setIsModalClosing(true);
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setActiveModal(null);
      setIsModalClosing(false);
    }, PROFILE_MODAL_CLOSE_MS);
  };

  const handleLogout = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const rememberToken = localStorage.getItem("rememberToken");
      await fetch(`${apiUrl}/logout.php`, {
        method: "POST",
        credentials: "include",
        headers: rememberToken
          ? {
              "X-Remember-Token": rememberToken
            }
          : undefined
      }).catch(() => {});
    } catch (error) {
      console.error("Logout error:", error);
    }

    localStorage.removeItem("user");
    localStorage.removeItem("rememberToken");
    requestCloseModal();
    navigate("/", { replace: true });
  };

  const handleProfileFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (profilePreviewUrl && profilePreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(profilePreviewUrl);
    }

    setSelectedProfileFile(file);
    setProfilePreviewUrl(URL.createObjectURL(file));
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl || !currentUser?.id) return;

    setIsSavingProfile(true);
    setProfileSaveError("");

    try {
      const form = new FormData();
      form.append("uid", currentUser.id);
      form.append("display_name", profileForm.displayName.trim());
      form.append("username", profileForm.username.trim());
      if (selectedProfileFile) {
        form.append("profile_picture", selectedProfileFile);
      }

      const rememberToken = localStorage.getItem("rememberToken") || "";
      const response = await fetch(`${apiUrl}/update-profile.php`, {
        method: "POST",
        credentials: "include",
        headers: rememberToken ? { "X-Remember-Token": rememberToken } : {},
        body: form
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setProfileSaveError(data?.error || "Could not update profile right now.");
        return;
      }

      const nextUser = {
        ...currentUser,
        ...data.user,
        profilePicture: getProfilePictureUrl(data.user?.profilePicture)
      };
      setCurrentUser(nextUser);
      localStorage.setItem("user", JSON.stringify(nextUser));
      window.dispatchEvent(new CustomEvent("profile-updated", { detail: nextUser }));
      requestCloseModal();
    } catch (error) {
      console.error("Failed to update profile:", error);
      setProfileSaveError("Could not update profile right now.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const displayName = formatDisplayName(currentUser?.displayName);
  const profileHandle = getProfileHandle(currentUser);
  const profilePicture = currentUser?.profilePicture || "";
  const collectionCards = COLLECTION_CARDS.map((card) => ({
    ...card,
    count: Number(listSummary?.[card.key]?.count || 0)
  }));

  return (
    <div className="profile-page app-shell-page">
      <div className="profile-page__content container-fluid px-3 px-md-4 pt-3 pb-5">
        <div className="profile-top d-flex align-items-center gap-3 gap-sm-4">
          <div className="profile-left">
            <div className="profile-avatar">
              {profilePicture ? (
                <img src={profilePicture} alt={`${displayName} profile`} />
              ) : (
                <span className="profile-avatar__letter">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          <div className="profile-right d-flex flex-column flex-grow-1">
            <div className="profile-user">
              <h2 className="mb-1 profile-user__heading">{`${displayName}'s Profile`}</h2>
            </div>
            <div className="profile-details fw-normal mb-3">
              {profileHandle
                ? `Twitter @: ${profileHandle}`
                : "Twitter @ is currently blank"}
            </div>

            <div className="profile-buttons d-flex flex-row align-items-stretch gap-2">
              <div className="edit-profile-btn flex-fill">
                <Button variant="cta" onClick={() => openModal("edit")}>
                  <span className="material-symbols-rounded profile-btn-icon profile-btn-icon--cta">
                    edit
                  </span>
                  Edit Profile
                </Button>
              </div>
              <div className="settings-btn flex-fill">
                <Button variant="neutral" onClick={() => openModal("settings")}>
                  <span className="material-symbols-rounded profile-btn-icon">
                    settings
                  </span>
                  Settings
                </Button>
              </div>
            </div>
          </div>
        </div>

        <section className="profile-section profile-section--divided mt-5 pt-4">
          <h3 className="mb-4 mt-2">Current Read</h3>

          {recentRead ? (
            <div
              className="upload-card-wrapper profile-recent-card p-4 d-flex gap-3 gap-sm-4 rounded-2"
              onClick={() => navigate(`/epub/${recentRead.id}`)}
            >
              <div className="uploads-left d-flex align-items-center justify-content-center">
                <span className="uploads-left-letter">
                  {recentRead.title?.charAt(0).toUpperCase()}
                </span>
              </div>

              <div className="uploads-middle d-flex flex-column flex-grow-1">
                <div className="profile-recent-card__content d-flex flex-column justify-content-between h-100">
                  <div>
                    <h5 className="mb-1 fw-medium">{recentRead.title}</h5>
                    <p className="uploads-author mb-0 fw-normal">{recentRead.author}</p>
                  </div>

                  <div className="profile-recent-card__progress">
                    <p className="recent-percent mb-2 fw-normal">
                      {getProgressLabel(recentRead.progress_percent)}
                    </p>
                    <div className="recent-progress-track mb-0">
                      <div
                        className="recent-progress-fill"
                        style={{ width: getProgressWidth(recentRead.progress_percent) }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn-neutral profile-empty-card text-start"
              onClick={() => navigate("/library")}
              disabled={recentLoading}
            >
              <span className="material-symbols-rounded profile-empty-card__icon">
                import_contacts
              </span>
              <span className="fw-semibold">
                {recentLoading ? "Loading recent read" : "Browse your library"}
              </span>
            </button>
          )}
        </section>

        <section className="profile-section mt-5">
          <h3 className="mb-4">Lists & Bookmarks</h3>

          <div className="profile-collections-grid">
            {collectionCards.map((card) => (
              <button
                key={card.key}
                type="button"
                className="btn-neutral profile-collection-card text-start"
                onClick={() => navigate(card.path)}
              >
                <div className="profile-collection-card__count-row d-flex align-items-end gap-2 mb-4">
                  <p className="profile-collection-card__number mb-0">{card.count}</p>
                  <p className="profile-collection-card__reads mb-1">reads</p>
                </div>
                <p className="mb-2 fw-medium profile-collection-card__title">{card.title}</p>
                <p className="mb-0 fw-normal profile-collection-card__subtitle">
                  {card.subtitle}
                </p>
              </button>
            ))}
          </div>
        </section>
      </div>

      {activeModal && (
        <div
          className={`profile-sheet-overlay${isModalClosing ? " profile-sheet-overlay--closing" : ""}`}
          onClick={requestCloseModal}
        >
          <div
            className={`profile-sheet${isModalClosing ? " profile-sheet--closing" : ""}`}
            onClick={(event) => event.stopPropagation()}
          >
            {activeModal === "settings" ? (
              <>
                <div className="profile-sheet__header">
                  <div>
                    <h3 className="mb-0">Settings</h3>
                  </div>
                </div>

                <div className="profile-sheet__body d-flex flex-column gap-3">
                  <button
                    type="button"
                    className="btn-neutral profile-sheet__ghost-btn text-start"
                    onClick={() => {
                      requestCloseModal();
                      navigate("/profile/general-rules");
                    }}
                  >
                    General Rules
                  </button>
                  <button
                    type="button"
                    className="profile-sheet__logout-btn"
                    onClick={handleLogout}
                  >
                    Log Out
                  </button>
                </div>
              </>
            ) : false ? (
              <>
                <div className="profile-sheet__header profile-sheet__header--rules align-items-center">
                  <button
                    type="button"
                    className="profile-sheet__back"
                    onClick={() => openModal("settings")}
                    aria-label="Back to settings"
                  >
                    <span className="material-symbols-rounded">arrow_back</span>
                  </button>
                  <h2 className="mb-0 profile-sheet__rules-title">General Rules</h2>
                </div>

                <div className="profile-sheet__body profile-sheet__body--rules px-4">
                  <div className="epub-subtitle mb-2">INFO</div>
                  <p className="epub-body-text mb-4">
                    While this is an app for both books and fics, please keep in mind most of its users are fixed shippers. Ensure fic uploads are limited to TMRHP and, in the case of ongoing fics, specify in the uploader notes if there’s any content people should be worried for as added chapters are Not part of the epub file itself, and thus don’t have updated tags.
                  </p>

                  <div className="epub-subtitle mb-2">ADDED CHAPTERS</div>
                  <p className="epub-body-text mb-4">
                    The app itself works best with complete fics/books, so to free up space in our servers, maybe wait for a fic to be complete before uploading it here? Added chapters take up more space than an epub file would (as it has everything already neatly zipped inside it), so if u can, avoid incomplete fics unless they’re already super long (like 100k+). This will help keep server costs for me low.
                  </p>

                  <div className="epub-subtitle mb-2">COMMENTARY</div>
                  <p className="epub-body-text mb-4">
                    And of course, there’s no limits to what content you can upload, go crazy with the dead dove idgaf, but be nice in the comments and Please use the export feature in the annotations tab to send a copy of your commentary to the author!
                  </p>

                  <div className="epub-subtitle mb-2">IMPORTANT</div>
                  <p className="epub-body-text mb-0">
                    Another thing to watch out for; if you see comments appearing paragraphs earlier than they should, it means the epub you uploaded was not correctly formatted to begin with. Comments happen on {"<p>"} tags, and if within those tags you have a break ({"<br>"}), the code will assume the whole thing is just one giant block of text even if sentences appear visually separated.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="profile-sheet__header">
                  <div>
                    <h3 className="mb-0">Edit Profile</h3>
                  </div>
                </div>

                <form className="profile-sheet__body profile-sheet__body--edit d-flex flex-column gap-3" onSubmit={handleProfileSave}>
                  <div className="profile-edit-avatar-row d-flex align-items-center gap-3">
                    <div
                      className="profile-avatar-wrap profile-avatar-wrap--editable"
                      onClick={() => fileInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                    >
                      <div className="profile-avatar profile-avatar--sheet">
                        {profilePreviewUrl ? (
                          <img src={profilePreviewUrl} alt="Profile preview" />
                        ) : (
                          <span className="profile-avatar__letter">
                            {formatDisplayName(profileForm.displayName).charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="profile-avatar__add-icon">
                        <span className="material-symbols-rounded">add</span>
                      </span>
                    </div>
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      <p className="profile-sheet__helper mb-0">
                        Uploading a new picture replaces the old one.
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="profile-sheet__file-input"
                      onChange={handleProfileFileChange}
                    />
                  </div>

                  <input
                    type="text"
                    className="btn-neutral"
                    placeholder="Display name"
                    value={profileForm.displayName}
                    onChange={(event) =>
                      setProfileForm((previous) => ({
                        ...previous,
                        displayName: event.target.value
                      }))
                    }
                  />

                  <input
                    type="text"
                    className="btn-neutral"
                    placeholder="Twitter username"
                    value={profileForm.username}
                    onChange={(event) =>
                      setProfileForm((previous) => ({
                        ...previous,
                        username: event.target.value.replace(/^@+/, "")
                      }))
                    }
                  />

                  {profileSaveError ? (
                    <p className="profile-sheet__error mb-0">{profileSaveError}</p>
                  ) : null}

                  <div className="profile-sheet__footer">
                    <button
                      type="submit"
                      className="btn-cta profile-sheet__save-btn"
                      disabled={isSavingProfile}
                    >
                      {isSavingProfile ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default ProfilePage;
